import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    StyleSheet
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    ToggleGroup,
    Carousel,
    Switch,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Carousel";

const USAGE_CODE = `<Carousel
    aria-label="Product tour"
    controlsPlacement="inside"
    indicator="dots"
    isLoop={true}
>
    <Carousel.Content>
        <Carousel.Item
            label="Overview"
        >
            <Card>
                <Card.Header>
                    <Card.Title>
                        Overview
                    </Card.Title>
                </Card.Header>
            </Card>
        </Carousel.Item>
        <Carousel.Item
            label="Reports"
        >
            <Card>
                <Card.Header>
                    <Card.Title>
                        Reports
                    </Card.Title>
                </Card.Header>
            </Card>
        </Carousel.Item>
    </Carousel.Content>
</Carousel>`;

const CONTROLLED_CODE = `const [activeIndex, setActiveIndex] = useState(0);

<Carousel
    onActiveIndexChange={setActiveIndex}
    activeIndex={activeIndex}
    aria-label="Product tour"
>
    <Carousel.Content>
        {slides}
    </Carousel.Content>
</Carousel>`;

const SLIDES = [
    {
        title: "carouselPage.slideOverview",
        key: "overview"
    },
    {
        title: "carouselPage.slideReports",
        key: "reports"
    },
    {
        title: "carouselPage.slideTeam",
        key: "team"
    },
    {
        title: "carouselPage.slideBilling",
        key: "billing"
    },
    {
        title: "carouselPage.slideSecurity",
        key: "security"
    },
    {
        title: "carouselPage.slideSettings",
        key: "settings"
    }
] as const;

const PLACEMENTS = [
    "inside",
    "outside",
    "end",
    "none"
] as const;

const INDICATORS = [
    "dots",
    "counter",
    "thumbnails",
    "badge",
    "none"
] as const;

const TOUR_SLIDE_COUNT = 4;

const AUTOPLAY_INTERVAL = 3000;

const AUTOPLAY_SECONDS = 3;

const VERTICAL_LENGTH = 190;

const getPosterStyle = ({
    colors
}: DocsStyleTokens): CSSProperties => ({
    background: colors.content.container.subtle,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    height: 120
});

const THUMBNAIL_STYLE: CSSProperties = {
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    height: "100%",
    width: "100%"
};

const PLAYGROUND_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
    width: "100%",
    gap: 16
};

const CONTROLS_STYLE: CSSProperties = {
    justifyContent: "center",
    flexWrap: "wrap",
    display: "flex",
    gap: 12
};

const GRID_STYLE: CSSProperties = {
    justifyContent: "center",
    alignItems: "flex-start",
    flexWrap: "wrap",
    display: "flex",
    gap: 24
};

const COLUMN_STYLE: CSSProperties = {
    alignItems: "flex-start",
    flexDirection: "column",
    display: "flex",
    gap: 12
};

const WIDE_COLUMN_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: "100%",
    gap: 12
};

const stylesheet = StyleSheet.create({
    wide: {
        maxWidth: 520,
        width: "100%"
    },
    full: {
        width: "100%"
    },
    narrow: {
        width: 270
    }
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("carouselPage.controlsTitle"),
        id: "controls"
    },
    {
        title: t("carouselPage.multipleTitle"),
        id: "multiple"
    },
    {
        title: t("carouselPage.autoplayTitle"),
        id: "autoplay"
    },
    {
        title: t("carouselPage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const CarouselPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        activeIndex,
        setActiveIndex
    ] = useState(0);

    const [
        placement,
        setPlacement
    ] = useState<typeof PLACEMENTS[number]>("inside");

    const [
        indicator,
        setIndicator
    ] = useState<typeof INDICATORS[number]>("dots");

    const [
        isAutoplayOn,
        setIsAutoplayOn
    ] = useState(false);

    const handleActiveIndexChange = (index: number) => setActiveIndex(index);

    const handlePlacementChange = (value: string | Array<string>) => setPlacement(PLACEMENTS.find((item) => item === value) ?? "inside");

    const handleIndicatorChange = (value: string | Array<string>) => setIndicator(INDICATORS.find((item) => item === value) ?? "dots");

    const handleToggleAutoplay = () => setIsAutoplayOn(!isAutoplayOn);

    const renderThumbnail = (index: number) => <div
        style={THUMBNAIL_STYLE}
    >
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {index + 1}
        </Text>
    </div>;

    const renderSlide = (slide: typeof SLIDES[number], index: number) => <Carousel.Item
        label={t(slide.title)}
        key={slide.key}
    >
        <Card
            size="small"
        >
            <Card.Header>
                <Card.Title>
                    {t(slide.title)}
                </Card.Title>
            </Card.Header>
            <Card.Content>
                <div
                    style={getPosterStyle(docsTokens)}
                >
                    <Text
                        variant="headlineLargeSize"
                        color="low"
                    >
                        {index + 1}
                    </Text>
                </div>
            </Card.Content>
        </Card>
    </Carousel.Item>;

    const renderLabeled = (label: string, node: ReactNode) => <div
        style={COLUMN_STYLE}
        key={label}
    >
        <InlineCode>
            {label}
        </InlineCode>
        {node}
    </div>;

    const renderWideLabeled = (label: string, node: ReactNode) => <div
        style={WIDE_COLUMN_STYLE}
        key={label}
    >
        <div>
            <InlineCode>
                {label}
            </InlineCode>
        </div>
        {node}
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("carouselPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("carouselPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("carouselPage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("carouselPage.overviewCaption", [
                    activeIndex + 1,
                    TOUR_SLIDE_COUNT
                ])}
                isStacked={true}
            >
                <Carousel
                    onActiveIndexChange={handleActiveIndexChange}
                    aria-label={t("carouselPage.label")}
                    activeIndex={activeIndex}
                    style={stylesheet.full}
                >
                    <Carousel.Content>
                        {SLIDES.slice(0, TOUR_SLIDE_COUNT).map(renderSlide)}
                    </Carousel.Content>
                </Carousel>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <CodeBlock
                code={CONTROLLED_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("carouselPage.controlsTitle")}
            id="controls"
        >
            <Paragraph>
                {t("carouselPage.controlsText")}
            </Paragraph>
            <Preview>
                <div
                    style={PLAYGROUND_STYLE}
                >
                    <Carousel
                        aria-label={t("carouselPage.label")}
                        renderThumbnail={renderThumbnail}
                        controlsPlacement={placement}
                        style={stylesheet.wide}
                        indicator={indicator}
                    >
                        <Carousel.Content>
                            {SLIDES.slice(0, TOUR_SLIDE_COUNT).map(renderSlide)}
                        </Carousel.Content>
                    </Carousel>
                    <div
                        style={CONTROLS_STYLE}
                    >
                        <ToggleGroup
                            title={t("carouselPage.placementLabel")}
                            onValueChange={handlePlacementChange}
                            attachBehaviour="attached"
                            isClearEnabled={false}
                            value={placement}
                            variant="outline"
                            size="small"
                        >
                            {
                                PLACEMENTS.map((item) => <ToggleGroup.Item
                                    title={item}
                                    value={item}
                                    key={item}
                                />)
                            }
                        </ToggleGroup>
                    </div>
                    <div
                        style={CONTROLS_STYLE}
                    >
                        <ToggleGroup
                            title={t("carouselPage.indicatorLabel")}
                            onValueChange={handleIndicatorChange}
                            attachBehaviour="attached"
                            isClearEnabled={false}
                            value={indicator}
                            variant="outline"
                            size="small"
                        >
                            {
                                INDICATORS.map((item) => <ToggleGroup.Item
                                    title={item}
                                    value={item}
                                    key={item}
                                />)
                            }
                        </ToggleGroup>
                    </div>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("carouselPage.multipleTitle")}
            id="multiple"
        >
            <Paragraph>
                {t("carouselPage.multipleText")}
            </Paragraph>
            <Preview
                caption={t("carouselPage.multipleCaption")}
            >
                <div
                    style={PLAYGROUND_STYLE}
                >
                    {
                        renderWideLabeled("visibleCount={3}", <Carousel
                            aria-label={t("carouselPage.label")}
                            style={stylesheet.full}
                            controlsPlacement="end"
                            indicator="counter"
                            visibleCount={3}
                        >
                            <Carousel.Content>
                                {SLIDES.map(renderSlide)}
                            </Carousel.Content>
                        </Carousel>)
                    }
                    {
                        renderWideLabeled("isPeek · isLoop", <Carousel
                            aria-label={t("carouselPage.label")}
                            controlsPlacement="none"
                            style={stylesheet.full}
                            isPeek={true}
                            isLoop={true}
                        >
                            <Carousel.Content>
                                {SLIDES.slice(0, TOUR_SLIDE_COUNT).map(renderSlide)}
                            </Carousel.Content>
                        </Carousel>)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("carouselPage.autoplayTitle")}
            id="autoplay"
        >
            <Paragraph>
                {t("carouselPage.autoplayText")}
            </Paragraph>
            <Preview
                caption={t("carouselPage.autoplayCaption", [
                    AUTOPLAY_SECONDS
                ])}
                isStacked={true}
            >
                <Carousel
                    aria-label={t("carouselPage.label")}
                    autoplayInterval={AUTOPLAY_INTERVAL}
                    isAutoplayEnabled={isAutoplayOn}
                    style={stylesheet.full}
                    isLoop={true}
                >
                    <Carousel.Content>
                        {SLIDES.slice(0, TOUR_SLIDE_COUNT).map(renderSlide)}
                    </Carousel.Content>
                </Carousel>
                <Switch
                    title={t("carouselPage.autoplayLabel")}
                    onPress={handleToggleAutoplay}
                    isChecked={isAutoplayOn}
                />
            </Preview>
        </Section>
        <Section
            title={t("carouselPage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("carouselPage.orientationText")}
            </Paragraph>
            <Preview
                caption={t("carouselPage.orientationCaption")}
                isStacked={true}
            >
                <Carousel
                    aria-label={t("carouselPage.label")}
                    length={VERTICAL_LENGTH}
                    style={stylesheet.full}
                    orientation="vertical"
                >
                    <Carousel.Content>
                        {SLIDES.slice(0, TOUR_SLIDE_COUNT).map(renderSlide)}
                    </Carousel.Content>
                </Carousel>
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("carouselPage.statesText")}
            </Paragraph>
            <Preview>
                <div
                    style={GRID_STYLE}
                >
                    {
                        renderLabeled("isLoading", <Carousel
                            aria-label={t("carouselPage.label")}
                            style={stylesheet.narrow}
                            controlsPlacement="end"
                            isLoading={true}
                            size="small"
                        >
                            <Carousel.Content />
                        </Carousel>)
                    }
                    {
                        renderLabeled(t("carouselPage.singleLabel"), <Carousel
                            aria-label={t("carouselPage.label")}
                            style={stylesheet.narrow}
                            controlsPlacement="end"
                            size="small"
                        >
                            <Carousel.Content>
                                {SLIDES.slice(0, 1).map(renderSlide)}
                            </Carousel.Content>
                        </Carousel>)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("carouselPage.a11yRegion"),
                    t("carouselPage.a11ySlides"),
                    t("carouselPage.a11yLive"),
                    t("carouselPage.a11yControls"),
                    t("carouselPage.a11yIndicators"),
                    t("carouselPage.a11yKeyboard"),
                    t("carouselPage.a11yAutoplay")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("carouselPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("carouselPage.propAriaLabel"),
                        name: "aria-label",
                        type: "string"
                    },
                    {
                        description: t("carouselPage.propActiveIndex"),
                        name: "activeIndex",
                        type: "number"
                    },
                    {
                        description: t("carouselPage.propDefaultActiveIndex"),
                        name: "defaultActiveIndex",
                        defaultValue: "0",
                        type: "number"
                    },
                    {
                        description: t("carouselPage.propOnActiveIndexChange"),
                        type: "(index: number) => void",
                        name: "onActiveIndexChange"
                    },
                    {
                        type: "\"inside\" | \"outside\" | \"end\" | \"none\"",
                        description: t("carouselPage.propControlsPlacement"),
                        defaultValue: "\"inside\"",
                        name: "controlsPlacement"
                    },
                    {
                        type: "\"dots\" | \"counter\" | \"thumbnails\" | \"badge\" | \"none\"",
                        description: t("carouselPage.propIndicator"),
                        defaultValue: "\"dots\"",
                        name: "indicator"
                    },
                    {
                        description: t("carouselPage.propRenderThumbnail"),
                        type: "(index: number) => ReactNode",
                        name: "renderThumbnail"
                    },
                    {
                        description: t("carouselPage.propVisibleCount"),
                        name: "visibleCount",
                        defaultValue: "1",
                        type: "1 | 2 | 3"
                    },
                    {
                        description: t("carouselPage.propIsPeek"),
                        defaultValue: "false",
                        name: "isPeek",
                        type: "boolean"
                    },
                    {
                        description: t("carouselPage.propIsLoop"),
                        defaultValue: "false",
                        name: "isLoop",
                        type: "boolean"
                    },
                    {
                        description: t("carouselPage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        description: t("carouselPage.propLength"),
                        name: "length",
                        type: "number"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("carouselPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("carouselPage.propIsAutoplayEnabled"),
                        name: "isAutoplayEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("carouselPage.propAutoplayInterval"),
                        name: "autoplayInterval",
                        type: "number"
                    },
                    {
                        description: t("carouselPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("carouselPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("carouselPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("carouselPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("carouselPage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("carouselPage.propPartLabel"),
                        name: "label",
                        type: "string"
                    },
                    {
                        description: t("carouselPage.propPartPlaceholder"),
                        name: "placeholder",
                        type: "ReactNode"
                    },
                    {
                        type: "\"dots\" | \"counter\" | \"thumbnails\" | \"badge\" | \"none\"",
                        description: t("carouselPage.propPartType"),
                        name: "type"
                    },
                    {
                        description: t("carouselPage.propPartRenderThumbnail"),
                        type: "(index: number) => ReactNode",
                        name: "renderThumbnail"
                    },
                    {
                        description: t("carouselPage.propPartAriaLabel"),
                        name: "aria-label",
                        type: "string"
                    },
                    {
                        description: t("carouselPage.propPartBorderRadius"),
                        name: "borderRadius",
                        type: "number"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default CarouselPage;
